/**
 * CalendarPickerPage duck — loads the host's listings, their
 * availability exceptions for the next 12 months, and their
 * upcoming bookings (sales transactions). Also exposes thunks for
 * creating a batch of exceptions (with progress + partial-failure
 * tracking) and deleting individual exceptions.
 *
 * IMPORTANT design notes:
 *
 * 1. Sharetribe's availabilityExceptions API has no batch endpoint;
 *    one create call per exception. For a host with 10 listings
 *    blocking "every Tuesday for 12 months" (= 520 exceptions) that's
 *    520 sequential network calls. We fan out with Promise.allSettled
 *    so a single failure doesn't kill the rest of the batch, and we
 *    track the failed inputs explicitly so the UI can retry ONLY
 *    those failures (not the whole batch — which would create
 *    duplicate exceptions for the ones that succeeded).
 *
 * 2. Booking and exception data are queried in parallel on load.
 *    The booking query is host-wide (sdk.transactions.query with
 *    only: 'sale'). Exceptions are per-listing — Promise.all across
 *    listings.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { storableError } from '../../util/errors';
import { denormalisedResponseEntities } from '../../util/data';
import { getStartOf } from '../../util/dates';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

const HORIZON_DAYS = 366;
const PER_PAGE = 100;

// ============================================================
// Helpers
// ============================================================

// Pull all pages of a listing's availability exceptions inside the
// horizon window. Sharetribe paginates with default ~10 per page, so
// hosts with lots of exceptions need follow-up calls.
const queryAllExceptionsForListing = (sdk, listingId, start, end) => {
  return sdk.availabilityExceptions
    .query({ listingId, start, end })
    .then(response => {
      const totalPages = response.data.meta.totalPages || 1;
      const firstPage = denormalisedResponseEntities(response);
      if (totalPages <= 1) return firstPage;

      const extraPages = [];
      for (let p = 2; p <= totalPages; p += 1) {
        extraPages.push(
          sdk.availabilityExceptions
            .query({ listingId, start, end, page: p })
            .then(r => denormalisedResponseEntities(r))
        );
      }
      return Promise.all(extraPages).then(rest => firstPage.concat(...rest));
    });
};

// ============================================================
// Async thunks
// ============================================================

// Loads everything needed to render the calendar:
//   - the host's listings
//   - exception sets per listing (next 12 months)
//   - upcoming sale transactions
const loadCalendarDataPayloadCreator = async (_, { extra: sdk, dispatch, rejectWithValue }) => {
  try {
    const now = new Date();
    const horizonStart = getStartOf(now, 'day', 'Etc/UTC');
    const horizonEnd = getStartOf(horizonStart, 'day', 'Etc/UTC', HORIZON_DAYS, 'days');

    // Listings + bookings can run in parallel.
    const [listingsResponse, transactionsResponse] = await Promise.all([
      sdk.ownListings.query({
        perPage: PER_PAGE,
        include: ['images'],
        'fields.image': ['variants.listing-card', 'variants.listing-card-2x'],
      }),
      sdk.transactions.query({
        only: 'sale',
        perPage: PER_PAGE,
        include: ['customer', 'listing'],
      }),
    ]);

    // Add transaction entities to the marketplace store so existing
    // selectors / TransactionCard usage elsewhere on the host
    // dashboard stays consistent.
    dispatch(addMarketplaceEntities(transactionsResponse));

    const listings = denormalisedResponseEntities(listingsResponse);
    const transactions = denormalisedResponseEntities(transactionsResponse);

    // Exceptions, per listing, in parallel.
    const exceptionsByListing = {};
    if (listings.length > 0) {
      const perListingResults = await Promise.all(
        listings.map(l =>
          queryAllExceptionsForListing(sdk, l.id, horizonStart, horizonEnd).then(exs => ({
            listingId: l.id.uuid,
            exceptions: exs,
          }))
        )
      );
      perListingResults.forEach(({ listingId, exceptions }) => {
        exceptionsByListing[listingId] = exceptions;
      });
    }

    return {
      listings,
      transactions,
      exceptionsByListing,
    };
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};

export const loadCalendarDataThunk = createAsyncThunk(
  'CalendarPickerPage/loadData',
  loadCalendarDataPayloadCreator
);

// Create a batch of exceptions, one SDK call per item, in parallel.
// Returns { successes, failures } so the UI can show progress and
// support retry-only-the-failures (per Phase 1 review feedback —
// re-running the whole batch would create duplicates for the ones
// that already succeeded).
//
// Progress reporting: the thunk dispatches `progressTick` actions
// every time a single create resolves (success or failure), so the
// UI can render "Saving block… 23 of 80".
const createExceptionsBatchPayloadCreator = (
  { items },
  { extra: sdk, dispatch, rejectWithValue }
) => {
  if (!Array.isArray(items) || items.length === 0) {
    return Promise.resolve({ successes: [], failures: [] });
  }

  dispatch(calendarPickerPageSlice.actions.batchStarted({ total: items.length }));

  const callOne = (item, idx) =>
    sdk.availabilityExceptions
      .create(
        { listingId: item.listingId, start: item.start, end: item.end, seats: item.seats },
        { expand: true }
      )
      .then(response => {
        dispatch(calendarPickerPageSlice.actions.progressTick());
        return {
          idx,
          ok: true,
          item,
          exception: response.data.data,
        };
      })
      .catch(e => {
        dispatch(calendarPickerPageSlice.actions.progressTick());
        return {
          idx,
          ok: false,
          item,
          error: storableError(e),
        };
      });

  return Promise.all(items.map((it, i) => callOne(it, i))).then(results => {
    const successes = results.filter(r => r.ok);
    const failures = results.filter(r => !r.ok);
    return { successes, failures };
  });
};

export const createExceptionsBatchThunk = createAsyncThunk(
  'CalendarPickerPage/createExceptionsBatch',
  createExceptionsBatchPayloadCreator
);

// Delete a single exception. The day-detail modal lets a host remove
// individual blocks (so they can undo "every Tuesday" one Tuesday at
// a time without rebuilding from scratch).
const deleteExceptionPayloadCreator = ({ exceptionId }, { extra: sdk, rejectWithValue }) => {
  return sdk.availabilityExceptions
    .delete({ id: exceptionId }, { expand: true })
    .then(response => ({ exceptionId, listingId: response.data.data.relationships?.listing?.data?.id }))
    .catch(e => rejectWithValue(storableError(e)));
};

export const deleteExceptionThunk = createAsyncThunk(
  'CalendarPickerPage/deleteException',
  deleteExceptionPayloadCreator
);

// ============================================================
// Slice
// ============================================================

const initialState = {
  loadInProgress: false,
  loadError: null,
  listings: [],
  transactions: [],
  // { [listingId]: AvailabilityException[] }
  exceptionsByListing: {},
  // Batch progress for the "Saving block…" modal.
  batchTotal: 0,
  batchDone: 0,
  batchInProgress: false,
  // Last batch's results — used to drive the retry button on
  // partial failures.
  batchSuccesses: [],
  batchFailures: [],
  // Single-exception delete state.
  deleteInProgress: null, // exception UUID being deleted
  deleteError: null,
};

const calendarPickerPageSlice = createSlice({
  name: 'CalendarPickerPage',
  initialState,
  reducers: {
    batchStarted: (state, action) => {
      state.batchTotal = action.payload.total;
      state.batchDone = 0;
      state.batchInProgress = true;
      state.batchSuccesses = [];
      state.batchFailures = [];
    },
    progressTick: state => {
      state.batchDone += 1;
    },
    clearBatchState: state => {
      state.batchTotal = 0;
      state.batchDone = 0;
      state.batchInProgress = false;
      state.batchSuccesses = [];
      state.batchFailures = [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadCalendarDataThunk.pending, state => {
        state.loadInProgress = true;
        state.loadError = null;
      })
      .addCase(loadCalendarDataThunk.fulfilled, (state, action) => {
        state.loadInProgress = false;
        state.listings = action.payload.listings;
        state.transactions = action.payload.transactions;
        state.exceptionsByListing = action.payload.exceptionsByListing;
      })
      .addCase(loadCalendarDataThunk.rejected, (state, action) => {
        state.loadInProgress = false;
        state.loadError = action.payload || action.error;
      });

    builder
      .addCase(createExceptionsBatchThunk.fulfilled, (state, action) => {
        state.batchInProgress = false;
        state.batchSuccesses = action.payload.successes;
        state.batchFailures = action.payload.failures;
        // Merge new exceptions into local state so the calendar
        // immediately reflects the blocks without a refetch.
        action.payload.successes.forEach(({ exception }) => {
          const lid = exception.relationships?.listing?.data?.id?.uuid;
          if (!lid) return;
          if (!state.exceptionsByListing[lid]) state.exceptionsByListing[lid] = [];
          state.exceptionsByListing[lid].push(exception);
        });
      })
      .addCase(createExceptionsBatchThunk.rejected, state => {
        state.batchInProgress = false;
      });

    builder
      .addCase(deleteExceptionThunk.pending, (state, action) => {
        state.deleteInProgress = action.meta.arg.exceptionId;
        state.deleteError = null;
      })
      .addCase(deleteExceptionThunk.fulfilled, (state, action) => {
        state.deleteInProgress = null;
        const { exceptionId } = action.payload;
        // Drop the deleted exception from whichever listing held it.
        Object.keys(state.exceptionsByListing).forEach(lid => {
          state.exceptionsByListing[lid] = state.exceptionsByListing[lid].filter(
            e => e.id?.uuid !== exceptionId.uuid
          );
        });
      })
      .addCase(deleteExceptionThunk.rejected, (state, action) => {
        state.deleteInProgress = null;
        state.deleteError = action.payload || action.error;
      });
  },
});

export const { clearBatchState } = calendarPickerPageSlice.actions;
export default calendarPickerPageSlice.reducer;

// ============================================================
// loadData entry point
// ============================================================

export const loadData = () => dispatch => {
  return dispatch(loadCalendarDataThunk()).unwrap();
};
